import React from "react";
import { FaExclamationTriangle, FaInfoCircle } from "react-icons/fa";
import "./SessionCreditEditor.css";

/**
 * SessionCreditEditor component for editing individual session type counters
 * Provides editable fields for remaining, booked, completed, forfeited, totalSessions, totalCompleted
 */
const SessionCreditEditor = ({
  title,
  sessionData = {},
  onUpdate,
  disabled = false,
  errors = {},
  fieldPrefix = "",
  isGiftCredit = false,
}) => {
  const {
    remaining = 0,
    booked = 0,
    completed = 0,
    forfeited = 0,
    totalSessions = 0,
    totalCompleted = 0,
    expiresAt = null,
  } = sessionData;

  // Calculate derived values
  const calculatedTotal = remaining + booked + completed + forfeited;
  const hasDiscrepancy = totalSessions !== calculatedTotal;
  const hasInvalidTotalCompleted = totalCompleted < completed;

  // Handle field updates
  const handleFieldChange = (field, value) => {
    const numericValue = parseInt(value) || 0;
    if (numericValue < 0) return; // Prevent negative values

    onUpdate(fieldPrefix, {
      ...sessionData,
      [field]: numericValue,
    });
  };

  // Get status based on session data
  const getStatus = () => {
    if (expiresAt && new Date(expiresAt) < new Date()) {
      return { text: "Expired", className: "expired" };
    }
    if (remaining > 0 || booked > 0) {
      return { text: "Active", className: "active" };
    }
    if (completed > 0 || forfeited > 0 || totalCompleted > 0) {
      return { text: "Used", className: "used" };
    }
    return { text: "Inactive", className: "inactive" };
  };

  const status = getStatus();

  return (
    <div
      className={`session-credit-editor ${disabled ? "disabled" : ""} ${
        isGiftCredit ? "gift-credit" : ""
      }`}
    >
      <div className="session-header">
        <h4>{title}</h4>
        <span className={`status-badge ${status.className}`}>
          {status.text}
        </span>
      </div>

      <div className="session-fields">
        <div className="field-row">
          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-remaining`}>Remaining</label>
            <input
              type="number"
              id={`${fieldPrefix}-remaining`}
              value={remaining}
              onChange={(e) => handleFieldChange("remaining", e.target.value)}
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.remaining`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.remaining`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.remaining`]}
              </span>
            )}
          </div>

          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-booked`}>Booked</label>
            <input
              type="number"
              id={`${fieldPrefix}-booked`}
              value={booked}
              onChange={(e) => handleFieldChange("booked", e.target.value)}
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.booked`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.booked`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.booked`]}
              </span>
            )}
          </div>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-completed`}>Completed</label>
            <input
              type="number"
              id={`${fieldPrefix}-completed`}
              value={completed}
              onChange={(e) => handleFieldChange("completed", e.target.value)}
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.completed`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.completed`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.completed`]}
              </span>
            )}
          </div>

          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-totalSessions`}>
              Total Sessions
            </label>
            <input
              type="number"
              id={`${fieldPrefix}-totalSessions`}
              value={totalSessions}
              onChange={(e) =>
                handleFieldChange("totalSessions", e.target.value)
              }
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.totalSessions`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.totalSessions`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.totalSessions`]}
              </span>
            )}
          </div>

          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-forfeited`}>Forfeited</label>
            <input
              type="number"
              id={`${fieldPrefix}-forfeited`}
              value={forfeited}
              onChange={(e) => handleFieldChange("forfeited", e.target.value)}
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.forfeited`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.forfeited`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.forfeited`]}
              </span>
            )}
          </div>
        </div>

        <div className="field-row">
          <div className="field-group">
            <label htmlFor={`${fieldPrefix}-totalCompleted`}>
              Total Completed (Lifetime)
            </label>
            <input
              type="number"
              id={`${fieldPrefix}-totalCompleted`}
              value={totalCompleted}
              onChange={(e) =>
                handleFieldChange("totalCompleted", e.target.value)
              }
              disabled={disabled}
              min="0"
              className={errors[`${fieldPrefix}.totalCompleted`] ? "error" : ""}
            />
            {errors[`${fieldPrefix}.totalCompleted`] && (
              <span className="error-message">
                {errors[`${fieldPrefix}.totalCompleted`]}
              </span>
            )}
            <small className="help-text">
              Lifetime counter - should be ≥ completed sessions
            </small>
          </div>
        </div>

        {/* Calculated values and warnings */}
        <div className="calculated-info">
          <div className="calculation">
            <FaInfoCircle />
            <span>
              Calculated Total: {calculatedTotal} sessions (Remaining + Booked +
              Completed)
            </span>
          </div>

          {hasDiscrepancy && (
            <div className="warning">
              <FaExclamationTriangle />
              <span>
                Warning: Total Sessions ({totalSessions}) doesn't match
                calculated total ({calculatedTotal})
              </span>
            </div>
          )}

          {hasInvalidTotalCompleted && (
            <div className="error-info">
              <FaExclamationTriangle />
              <span>
                Error: Total Completed ({totalCompleted}) cannot be less than
                current completed ({completed})
              </span>
            </div>
          )}

          {expiresAt && (
            <div className="expiry-info">
              <FaInfoCircle />
              <span>Expires: {new Date(expiresAt).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionCreditEditor;
